/* eslint-disable*/
import { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Tooltip,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DownloadIcon from '@mui/icons-material/Download';
import download_icon from '../../organisms/layout/dashboard/icons/download_icon.svg';

import NewButtongroup from 'components/atoms/group/NewButtongroup';
import NewPullDown from 'components/atoms/button/NewPullDown';
import NewDialogSelect from 'components/atoms/select/NewDialogSelect';
import FavoriteCategoryPullDown from 'components/atoms/button/FavoriteCategoryPullDown';

import RankingTotal from 'components/organisms/content/category/RankingTotal';
import CategoryTotal from 'components/organisms/content/category/CategoryTotal';
import MarketLabel from 'components/molecules/dashboard/MarketLabel';
import CategorySearchNeedsSection from 'components/molecules/market/CategorySearchNeedsSection';
import FreePlanRegisterModal from 'components/atoms/modal/FreePlanRegisterModal';
import StickyAnnotation from 'components/atoms/annotation/StickyAnnotation';

import { currentchannel } from 'feactures/channel/channelSlice';

import {
  amazonMonthList,
  rakutenMonthList,
  ensureYearMonthForChannel
} from 'feactures/section/sectionSlice';

import {
  amazonCategory,
  rakutenCategory,
  yahooCategory
} from 'feactures/category/categorySlice';

import {
  amazon_100items,
  rakuten_100items,
  yahoo_100items,
  categoryRankStatus as top100Status,
  fetchCategoryRankData
} from 'feactures/category/categoryRankSlice';

import {
  categorySalesStatus,
  categorygraphStatus,
  fetchCategorySalesGraph,
  fetchCategoryGraphData,
  fetchSalesHistoryTable,
  selectAmazonCategorySalesGraph,
  selectRakutenCategorySalesGraph,
  selectYahooCategorySalesGraph,
  currentAmazonCategoryList,
  currentRakutenCategoryList,
  currentYahooCategoryList,
  salesHistoryStatusSelector
} from 'feactures/category/categoryGraphSlice';

// --- 定数とユーティリティ関数 ---
const ROW1_HEIGHT = 460;
const fmtYen = (n) =>
  Number.isFinite(n) ? `¥${n.toLocaleString('ja-JP')}` : 'N/A';

const deriveTermFromYokoziku = (yokoziku, fallback = '2025_01') => {
  if (!yokoziku) return fallback;
  if (typeof yokoziku === 'string') return yokoziku;
  if (
    typeof yokoziku === 'object' &&
    !Array.isArray(yokoziku) &&
    yokoziku.value
  )
    return yokoziku.value;
  if (Array.isArray(yokoziku) && yokoziku.length > 0) {
    const first = yokoziku[0];
    if (typeof first === 'string') return first;
    if (first && typeof first === 'object' && first.value) return first.value;
  }
  return fallback;
};

// 'YYYY_MM' or 'YYYY-MM' -> sortable key
const ymToKey = (s) => {
  const str = String(s || '').trim();
  const m = str.match(/([0-9]{4})[^0-9]?([0-9]{1,2})/);
  if (!m) return -Infinity;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  return y * 100 + mo;
};

// ---------- 円グラフ ----------
const PieSVG = ({
  rows,
  total,
  colors,
  size = 300,
  onHoverSlice,
  onLeaveSlice
}) => {
  const r = size / 2;
  const cx = r;
  const cy = r;
  let acc = 0;

  const toXY = (angle, radius = r) => [
    cx + radius * Math.cos(angle - Math.PI / 2),
    cy + radius * Math.sin(angle - Math.PI / 2)
  ];

  const paths = rows.map((row, i) => {
    const v = Number(row.value || 0);
    const angle = total > 0 ? (v / total) * Math.PI * 2 : 0;

    if (angle >= Math.PI * 2 - 1e-6) {
      const handleEnter = () => {
        onHoverSlice &&
          onHoverSlice({ name: row.name ?? '—', value: v, xPct: 50, yPct: 35 });
      };
      return (
        <circle
          key={i}
          cx={cx}
          cy={cy}
          r={r}
          fill={colors[i % colors.length]}
          onMouseEnter={handleEnter}
          onMouseMove={handleEnter}
          onMouseLeave={onLeaveSlice}
        />
      );
    }

    const start = acc;
    const end = acc + angle;
    acc = end;

    const [x1, y1] = toXY(start);
    const [x2, y2] = toXY(end);
    const largeArc = angle > Math.PI ? 1 : 0;
    const d = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;

    const mid = (start + end) / 2;
    const [mx, my] = toXY(mid, r * 0.65);
    const handleEnter = () => {
      onHoverSlice &&
        onHoverSlice({
          name: row.name ?? '—',
          value: v,
          xPct: (mx / size) * 100,
          yPct: (my / size) * 100
        });
    };

    return (
      <path
        key={i}
        d={d}
        fill={colors[i % colors.length]}
        onMouseEnter={handleEnter}
        onMouseMove={handleEnter}
        onMouseLeave={onLeaveSlice}
      />
    );
  });

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label="売上内訳円グラフ"
      style={{ display: 'block' }}
    >
      {paths}
    </svg>
  );
};

const SummaryPie = ({
  data = [],
  loading = false,
  isLeafSelected = false,
  title = 'カテゴリ売上内訳（上位5カテゴリー）',
  pieScale = 0.9,
  maxPieSize = 300,
  showLegend = true
}) => {
  const toNum = (x) => {
    if (x == null) return 0;
    if (typeof x === 'number') return Number.isFinite(x) ? x : 0;
    const n = Number(String(x).replace(/[^\d.-]/g, ''));
    return Number.isFinite(n) ? n : 0;
  };

  const all = Array.isArray(data)
    ? data
        .map((r) => ({
          name: r?.category_name ?? r?.name ?? '—',
          value: toNum(r?.sales ?? r?.value ?? 0)
        }))
        .filter((r) => r.value > 0)
    : [];

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (all.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1
        }}
      >
        <Typography color="textSecondary">
          {isLeafSelected
            ? '最下層のカテゴリーのため表示できません。'
            : 'データがありません。'}
        </Typography>
      </Box>
    );
  }

  const sorted = all.slice().sort((a, b) => b.value - a.value);
  const top5 = sorted.slice(0, 5);
  const othersValue = sorted.slice(5).reduce((s, r) => s + r.value, 0);
  const rows =
    othersValue > 0
      ? [...top5, { name: 'その他', value: othersValue, isOther: true }]
      : top5;

  const COLORS_MAIN = ['#332288', '#88CCEE', '#44AA99', '#DDCC77', '#CC6677'];
  const COLOR_OTHER = '#9CA3AF';
  const colors = rows.map((r, i) =>
    r.isOther ? COLOR_OTHER : COLORS_MAIN[i % COLORS_MAIN.length]
  );
  const total = rows.reduce((s, r) => s + r.value, 0);

  const [tip, setTip] = useState(null);
  const leftPaneRef = useRef(null);
  const [pieSize, setPieSize] = useState(240);

  useEffect(() => {
    const el = leftPaneRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cr = entry.contentRect || {};
        const h = cr.height || el.clientHeight || 0;
        const w = cr.width || el.clientWidth || 0;
        const base = Math.min(w, h || w);
        const size = Math.floor(base * pieScale);
        const clipped = Math.min(size, maxPieSize);
        setPieSize(Math.max(120, clipped));
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [pieScale, maxPieSize]);

  const emptyMessage = isLeafSelected
    ? '最下層のカテゴリーのため表示できません。'
    : 'データがありません。';

  return (
    <Box width="100%" height="100%" display="flex" flexDirection="column">
      {!!title && (
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 'bold',
            mb: 2.5,
            mt: -0.75,
            ml: -0.75
          }}
        >
          {title}
        </Typography>
      )}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'stretch',
          justifyContent: 'space-between',
          gap: 1.25,
          minHeight: 0,
          minWidth: 0
        }}
      >
        <Box
          ref={leftPaneRef}
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            minHeight: 0,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {!rows.length || total === 0 ? (
            <Typography color="textSecondary">{emptyMessage}</Typography>
          ) : (
            <PieSVG
              rows={rows}
              total={total}
              colors={colors}
              size={pieSize}
              onHoverSlice={(info) => setTip(info)}
              onLeaveSlice={() => setTip(null)}
            />
          )}

          {tip && (
            <Box
              sx={{
                position: 'absolute',
                left: `${tip.xPct}%`,
                top: `${tip.yPct}%`,
                transform: 'translate(-50%, -10%)',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                bgcolor: '#111827',
                color: '#fff',
                fontSize: 12,
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
                boxShadow: 2
              }}
            >
              <div style={{ fontWeight: 600 }}>{tip.name}</div>
              <div>{fmtYen(tip.value)}</div>
            </Box>
          )}
        </Box>

        {!!rows.length && total > 0 && showLegend && (
          <Box
            sx={{
              flex: { xs: '0 0 auto', sm: '0 1 42%' },
              maxWidth: { xs: '100%', sm: '42%' },
              minWidth: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
              alignItems: 'stretch',
              justifyContent: 'center',
              boxSizing: 'border-box',
              pr: 0.5
            }}
          >
            {rows.map((r, i) => (
              <Box
                key={`${r.name}-${i}`}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  minWidth: 0,
                  width: '100%'
                }}
              >
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    bgcolor: colors[i],
                    mr: 1,
                    mt: '3px',
                    flexShrink: 0
                  }}
                />
                <Typography
                  title={r.name}
                  sx={{
                    fontSize: 14,
                    minWidth: 0,
                    flex: 1,
                    lineHeight: 1.35,
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word'
                  }}
                >
                  {r.name}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// --- NewCategoryboard ---
const NewCategoryboard = ({
  freePlanRegisterModalOpen,
  updateFreePlanRegisterInfosSuccessFlag,
  downloadIconSrc = null
}) => {
  const dispatch = useDispatch();
  const yearMonth = useSelector((s) => s.currentsection.yearMonth);
  const amazonList = useSelector(amazonMonthList);
  const rakutenList = useSelector(rakutenMonthList);

  // グローバル
  const channel = useSelector(currentchannel);
  const accountId = useSelector((s) => s.user.accountId);

  // 候補カテゴリ
  const amazonCategories = useSelector(amazonCategory);
  const rakutenCategories = useSelector(rakutenCategory);
  const yahooCategories = useSelector(yahooCategory);

  // 選択パス
  const amazonPath = useSelector(currentAmazonCategoryList, shallowEqual);
  const rakutenPath = useSelector(currentRakutenCategoryList, shallowEqual);
  const yahooPath = useSelector(currentYahooCategoryList, shallowEqual);

  // ローディング
  const graphloading = useSelector(categorygraphStatus);
  const categorySalesLoading = useSelector(categorySalesStatus);

  // ▼ TOP100（ランキング）
  const a100 = useSelector(amazon_100items) || [];
  const r100 = useSelector(rakuten_100items) || [];
  const y100 = useSelector(yahoo_100items) || [];
  const top100loading = useSelector(top100Status);

  // ローカル
  const [yokoziku, setYokoziku] = useState([]);
  const [viewType, setViewType] = useState('category'); // 'category' | 'top5'
  const [selectDate, setSelectDate] = useState('2025_01');
  const [salesBusy, setSalesBusy] = useState(false);
  const [rankBusy, setRankBusy] = useState(false);
  const [graphBusy, setGraphBusy] = useState(false);
  const [loadedSalesKey, setLoadedSalesKey] = useState('');
  const [loadedRankKey, setLoadedRankKey] = useState('');
  const [loadedGraphKey, setLoadedGraphKey] = useState('');

  // 市場規模詳細（表）データ
  const categorySalesHistory =
    useSelector((s) => s?.categorygraph?.salesHistory) || [];
  const salesHistoryStatus = useSelector(salesHistoryStatusSelector);

  const normalizedHistory = useMemo(() => {
    const rows = Array.isArray(categorySalesHistory)
      ? categorySalesHistory
      : Array.isArray(categorySalesHistory?.data)
      ? categorySalesHistory.data
      : [];
    return rows.map((r) => ({
      ym: r?.year_month ?? r?.yearMonth ?? '—',
      sales: Number(r?.sales ?? r?.value ?? NaN),
      sales_last_year: Number(r?.sales_last_year ?? r?.salesLastYear ?? NaN),
      brand_count: Number(r?.brand_count ?? NaN)
    }));
  }, [categorySalesHistory]);

  // 市場規模詳細テーブル: 最新の月(降順)
  const historySortedDesc = useMemo(() => {
    const arr = Array.isArray(normalizedHistory) ? [...normalizedHistory] : [];
    arr.sort((a, b) => ymToKey(b.ym) - ymToKey(a.ym));
    return arr;
  }, [normalizedHistory]);

  // UIカテゴリ
  const categories = useMemo(() => {
    if (channel === 1) return amazonCategories || [];
    if (channel === 2) return rakutenCategories || [];
    if (channel === 3) return yahooCategories || [];
    return [];
  }, [channel, amazonCategories, rakutenCategories, yahooCategories]);

  const selectedList = useMemo(() => {
    if (channel === 1) return Array.isArray(amazonPath) ? amazonPath : [];
    if (channel === 2) return Array.isArray(rakutenPath) ? rakutenPath : [];
    if (channel === 3) return Array.isArray(yahooPath) ? yahooPath : [];
    return [];
  }, [channel, amazonPath, rakutenPath, yahooPath]);

  // カテゴリ別サマリー取得
  const selectorMap = {
    1: selectAmazonCategorySalesGraph,
    2: selectRakutenCategorySalesGraph,
    3: selectYahooCategorySalesGraph
  };
  const summaryPayload = useSelector(selectorMap[channel] ?? (() => []));
  const summaryRows = useMemo(() => {
    if (Array.isArray(summaryPayload)) return summaryPayload;
    if (summaryPayload && typeof summaryPayload === 'object') {
      if (Array.isArray(summaryPayload.rows)) return summaryPayload.rows;
      if (Array.isArray(summaryPayload.data)) return summaryPayload.data;
    }
    return [];
  }, [summaryPayload]);
  const isLeafSelected = !!(
    summaryPayload &&
    typeof summaryPayload === 'object' &&
    summaryPayload.is_leaf === true
  );

  // yokoziku -> term 推定
  useEffect(() => {
    const derived = deriveTermFromYokoziku(yokoziku, selectDate || '2025_01');
    if (derived && derived !== selectDate) setSelectDate(derived);
  }, [yokoziku, selectDate]);

  // 選択キー（依存判定用）
  const selectedKey = useMemo(() => {
    const arr = (selectedList || []).filter((v) => v != null && v !== 0);
    return arr.join('-') || 'root';
  }, [selectedList]);

  const currentDataKey = useMemo(
    () => `${channel}|${yearMonth}|${selectedKey}`,
    [channel, yearMonth, selectedKey]
  );

  const isMonthValidForChannel = useMemo(() => {
    if (channel === 3) return true;
    if (channel !== 1 && channel !== 2) return false;
    const list = channel === 1 ? amazonList : rakutenList;
    if (!Array.isArray(list) || list.length === 0) return false;
    return list.includes(yearMonth);
  }, [channel, yearMonth, amazonList, rakutenList]);

  // 月リストに yearMonth が無いときだけ補正（チャネル切替の復元は registerAmazon/Rakuten で実施）
  useLayoutEffect(() => {
    if (channel !== 1 && channel !== 2) return;

    const list = channel === 1 ? amazonList : rakutenList;
    if (!Array.isArray(list) || list.length === 0) return;
    if (list.includes(yearMonth)) return;

    dispatch(ensureYearMonthForChannel(channel));
  }, [channel, yearMonth, amazonList, rakutenList, dispatch]);

  // APIトリガー（カテゴリ別サマリー）
  const lastReqKeyRef = useRef('');
  const lastRankReqKeyRef = useRef('');
  const lastHistoryReqKeyRef = useRef('');
  const lastGraphReqKeyRef = useRef('');
  const isSummaryLoading =
    categorySalesLoading ||
    salesBusy ||
    (isMonthValidForChannel && loadedSalesKey !== currentDataKey);

  useEffect(() => {
    lastReqKeyRef.current = '';
    lastRankReqKeyRef.current = '';
    lastHistoryReqKeyRef.current = '';
    lastGraphReqKeyRef.current = '';
    setLoadedSalesKey('');
    setLoadedRankKey('');
    setLoadedGraphKey('');
    setSalesBusy(false);
    setRankBusy(false);
    setGraphBusy(false);
  }, [channel]);

  const isGraphLoading =
    graphloading ||
    graphBusy ||
    (isMonthValidForChannel && loadedGraphKey !== currentDataKey);

  // APIトリガー（市場規模推移グラフ）
  useEffect(() => {
    if (!isMonthValidForChannel) return;
    if (lastGraphReqKeyRef.current === currentDataKey) return;

    let active = true;
    setGraphBusy(true);
    const promise = dispatch(fetchCategoryGraphData());

    promise
      .unwrap()
      .then(() => {
        if (!active) return;
        lastGraphReqKeyRef.current = currentDataKey;
        setLoadedGraphKey(currentDataKey);
      })
      .catch((e) => {
        if (!active) return;
        if (e?.name !== 'AbortError' && e?.code !== 'ERR_CANCELED') {
          console.error('fetchCategoryGraphData failed:', e);
        }
      })
      .finally(() => {
        if (active) setGraphBusy(false);
      });

    return () => {
      active = false;
      promise.abort();
    };
  }, [dispatch, currentDataKey, isMonthValidForChannel]);

  useEffect(() => {
    if (!isMonthValidForChannel) return;
    if (lastReqKeyRef.current === currentDataKey) return;

    let active = true;
    setSalesBusy(true);
    const promise = dispatch(fetchCategorySalesGraph());

    promise
      .unwrap()
      .then(() => {
        if (!active) return;
        lastReqKeyRef.current = currentDataKey;
        setLoadedSalesKey(currentDataKey);
      })
      .catch((e) => {
        if (!active) return;
        if (e?.name !== 'AbortError' && e?.code !== 'ERR_CANCELED') {
          console.error(e);
        }
      })
      .finally(() => {
        if (active) setSalesBusy(false);
      });

    return () => {
      active = false;
      promise.abort();
    };
  }, [dispatch, currentDataKey, isMonthValidForChannel]);

  // APIトリガー（市場規模詳細：表）
  useEffect(() => {
    if (!isMonthValidForChannel) return;
    if (lastHistoryReqKeyRef.current === currentDataKey) return;

    let active = true;
    const promise = dispatch(fetchSalesHistoryTable());

    promise
      .unwrap()
      .then(() => {
        if (!active) return;
        lastHistoryReqKeyRef.current = currentDataKey;
      })
      .catch((e) => {
        if (!active) return;
        if (e?.name !== 'AbortError' && e?.code !== 'ERR_CANCELED') {
          console.error('fetchSalesHistoryTable failed:', e);
        }
      });

    return () => {
      active = false;
      promise.abort();
    };
  }, [dispatch, currentDataKey, isMonthValidForChannel]);

  const isRankLoading =
    top100loading ||
    rankBusy ||
    (isMonthValidForChannel && loadedRankKey !== currentDataKey);

  // APIトリガー（TOP100）
  useEffect(() => {
    if (!isMonthValidForChannel) return;
    if (lastRankReqKeyRef.current === currentDataKey) return;

    let active = true;
    setRankBusy(true);
    const promise = dispatch(fetchCategoryRankData());

    promise
      .unwrap()
      .then(() => {
        if (!active) return;
        lastRankReqKeyRef.current = currentDataKey;
        setLoadedRankKey(currentDataKey);
      })
      .catch((e) => {
        if (!active) return;
        if (e?.name !== 'AbortError' && e?.code !== 'ERR_CANCELED') {
          console.error(e);
        }
      })
      .finally(() => {
        if (active) setRankBusy(false);
      });

    return () => {
      active = false;
      promise.abort();
    };
  }, [dispatch, currentDataKey, isMonthValidForChannel]);

  // SummaryPie に渡すデータ（viewTypeに応じて切替）
  const pieData = useMemo(() => {
    const toNum = (x) => {
      if (x == null) return 0;
      if (typeof x === 'number') return Number.isFinite(x) ? x : 0;
      const n = Number(String(x).replace(/[^\d.-]/g, ''));
      return Number.isFinite(n) ? n : 0;
    };

    if (viewType === 'top5') {
      const src =
        channel === 1 ? a100 : channel === 2 ? r100 : channel === 3 ? y100 : [];

      return (Array.isArray(src) ? src : []).map((item) => ({
        name: item?.name ?? '—',
        sales: toNum(item?.sales ?? 0)
      }));
    }

    return Array.isArray(summaryRows) ? summaryRows : [];
  }, [viewType, summaryRows, channel, a100, r100, y100]);

  // サマリー表：並び替え & ページネーション
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const handleSort = (key) => {
    setSortDir((prev) =>
      sortKey === key ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'
    );
    setSortKey(key);
    setPage(0);
  };

  const sortedSummaryData = useMemo(() => {
    const base = summaryRows;
    if (!base || !Array.isArray(base)) return [];
    if (!sortKey) return base;
    const arr = [...base];
    arr.sort((a, b) => {
      const avRaw = Number(a?.[sortKey]);
      const bvRaw = Number(b?.[sortKey]);
      const av = Number.isFinite(avRaw)
        ? avRaw
        : sortDir === 'asc'
        ? Number.POSITIVE_INFINITY
        : Number.NEGATIVE_INFINITY;
      const bv = Number.isFinite(bvRaw)
        ? bvRaw
        : sortDir === 'asc'
        ? Number.POSITIVE_INFINITY
        : Number.NEGATIVE_INFINITY;
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return arr;
  }, [summaryRows, sortKey, sortDir]);

  const downloadCategorySummaryCsv = useCallback(() => {
    try {
      const header = ['No', 'カテゴリ', '売上', 'シェア', '前年比'];
      const rows = (sortedSummaryData || []).map((row, idx) => {
        const name = row?.category_name ?? '';
        const sales = Number(row?.sales ?? 0);
        const share = Number(row?.sales_share ?? NaN);
        const yoy = Number(row?.yoy ?? NaN);
        const safeName = String(name).replace(/"/g, '""');
        return [
          String(idx + 1),
          `"${safeName}"`,
          Number.isFinite(sales) ? String(sales) : '',
          Number.isFinite(share) ? String(share) : '',
          Number.isFinite(yoy) ? String(yoy) : ''
        ];
      });
      const csv = [header.join(','), ...rows.map((r) => r.join(','))].join(
        '\n'
      );
      const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
      const blob = new Blob([bom, csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ch = String(channel);
      const ym = String(yearMonth || '').replaceAll('/', '_');
      a.href = url;
      a.download = `category_sales_${ch}_${ym}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('CSV download failed', e);
    }
  }, [sortedSummaryData, channel, yearMonth]);

  const handleChangePage = (_e, newPage) => setPage(newPage);

  const pagedSummaryData = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedSummaryData.slice(start, start + rowsPerPage);
  }, [sortedSummaryData, page]);

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(sortedSummaryData.length / rowsPerPage) - 1
    );
    if (page > maxPage) setPage(Math.max(0, maxPage));
  }, [sortedSummaryData.length, page]);

  // ▼ カテゴリ別サマリー：テーブル + CSV（ダウンロードは前年比ヘッダー真上）
  const renderSummaryTable = (isLoading) => {
    if (isLoading) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 200
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column'
            }}
          >
            <p>サマリーの読み込み中…</p>
            <CircularProgress />
          </Box>
        </Box>
      );
    }

    const rowsAll = Array.isArray(summaryRows) ? summaryRows : [];
    if (rowsAll.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 200
          }}
        >
          <Typography>データがありません。</Typography>
        </Box>
      );
    }

    const totals = rowsAll.reduce(
      (acc, row) => {
        acc.sales += Number(row?.sales ?? 0);
        acc.share += Number(row?.sales_share ?? 0);
        return acc;
      },
      { sales: 0, share: 0 }
    );

    const startIndex = page * rowsPerPage;

    return (
      <>
        <TableContainer sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
          <Table size="small" sx={{ minWidth: 480 }}>
            <TableHead>
              <TableRow>
                <TableCell width={64} align="right" sx={{ whiteSpace: 'nowrap' }}>
                  No
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>カテゴリ</TableCell>
                <TableCell
                  align="right"
                  onClick={() => handleSort('sales')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  売上{' '}
                  {sortKey === 'sales' ? (
                    sortDir === 'asc' ? (
                      <ArrowUpwardIcon fontSize="small" />
                    ) : (
                      <ArrowDownwardIcon fontSize="small" />
                    )
                  ) : (
                    ''
                  )}
                </TableCell>
                <TableCell
                  align="right"
                  onClick={() => handleSort('sales_share')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  シェア{' '}
                  {sortKey === 'sales_share' ? (
                    sortDir === 'asc' ? (
                      <ArrowUpwardIcon fontSize="small" />
                    ) : (
                      <ArrowDownwardIcon fontSize="small" />
                    )
                  ) : (
                    ''
                  )}
                </TableCell>
                <TableCell
                  align="right"
                  onClick={() => handleSort('yoy')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  前年比{' '}
                  {sortKey === 'yoy' ? (
                    sortDir === 'asc' ? (
                      <ArrowUpwardIcon fontSize="small" />
                    ) : (
                      <ArrowDownwardIcon fontSize="small" />
                    )
                  ) : (
                    ''
                  )}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {/* 合計行：5列構成 */}
              <TableRow
                sx={{
                  backgroundColor: '#f5f5f5',
                  '& td': { fontWeight: 'bold' }
                }}
              >
                <TableCell align="left" colSpan={2}>
                  合計
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {fmtYen(totals.sales)}
                </TableCell>
                <TableCell align="right">100%</TableCell>
                <TableCell align="right">-</TableCell>
              </TableRow>

              {pagedSummaryData.map((row, index) => {
                const name = row?.category_name ?? '—';
                const sales = Number(row?.sales ?? 0);
                const share = Number(row?.sales_share ?? 0);
                const yoy = Number(row?.yoy ?? NaN);

                return (
                  <TableRow key={`${name}-${startIndex + index}`}>
                    <TableCell align="right">
                      {startIndex + index + 1}
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 180,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={name}
                    >
                      {name}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {fmtYen(sales)}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {Number.isFinite(share) ? `${share.toFixed(2)}%` : 'N/A'}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {Number.isFinite(yoy) ? `${yoy.toFixed(2)}%` : 'N/A'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box display="flex" justifyContent="flex-end">
          <TablePagination
            component="div"
            count={sortedSummaryData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[rowsPerPage]}
            onRowsPerPageChange={() => {}}
            labelRowsPerPage=""
          />
        </Box>
      </>
    );
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: '#FFF8E5',
          minHeight: '100vh',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          pb: '80px',
          px: { xs: 1.5, sm: 2, md: 3 }
        }}
      >
        <Container
          maxWidth={false}
          disableGutters
          sx={{ width: '100%', maxWidth: '100%', p: 0, boxSizing: 'border-box' }}
        >
          {/* チャンネル・カテゴリ選択：スクロール追従 */}
          <Box
            position="sticky"
            top="42px"
            bgcolor="#FFF8E5"
            zIndex={5}
            sx={{
              width: '100%',
              maxWidth: '100%',
              pt: 0.5,
              pb: 2,
              boxSizing: 'border-box'
            }}
          >
            {channel == 1 && <StickyAnnotation />}
            <Box display="flex" alignItems="center" height="33px" mb={1}>
              <Typography fontSize="14px">マーケットビュー</Typography>
              <ArrowForwardIosOutlinedIcon
                sx={{ fontSize: '16px', mx: 1, color: 'gray' }}
              />
              <Typography fontSize="14px" fontWeight="bold">
                カテゴリ分析
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-end',
                columnGap: '24px',
                rowGap: '16px',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                borderTop: 1,
                borderColor: '#FFFFFF',
                pt: 1.5
              }}
            >
              <Box sx={{ flex: '0 0 auto' }}>
                <NewButtongroup amazonOnly={true} page="category" />
              </Box>

              {!freePlanRegisterModalOpen && (
                <>
                  <Box sx={{ flex: '0 0 auto' }}>
                    <NewPullDown
                      page="category"
                      label="期間"
                      setYokoziku={setYokoziku}
                      graphLoading={top100loading}
                    />
                  </Box>

                  <Box sx={{ flex: '0 0 auto' }}>
                    <Typography
                      fontSize="14px"
                      sx={{ mb: 0.75, display: 'block' }}
                    >
                      カテゴリを選択する
                    </Typography>
                    <NewDialogSelect
                      categories={categories}
                      channel={channel}
                      page="category"
                      graphLoading={top100loading}
                    />
                  </Box>

                  <Box sx={{ flex: '0 0 auto' }}>
                    <Typography
                      fontSize="14px"
                      sx={{ mb: 0.75, display: 'block' }}
                    >
                      お気に入りカテゴリ
                    </Typography>
                    <FavoriteCategoryPullDown
                      page="category"
                      graphLoading={top100loading}
                    />
                  </Box>
                </>
              )}
            </Box>
          </Box>

          {!freePlanRegisterModalOpen ? (
            <>
              {/* 選択中カテゴリ（ページ共通・1回だけ） */}
              <Box sx={{ mt: 1.5, mb: 2, minWidth: 0 }}>
                <MarketLabel />
              </Box>

              {/* 1段目：市場規模推移｜市場規模詳細 */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                  gap: 3,
                  mb: 3,
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    minWidth: 0,
                    maxWidth: '100%'
                  }}
                >
                  <Box
                    bgcolor="#FFFFFF"
                    borderRadius={3}
                    sx={{
                      p: 2,
                      minHeight: ROW1_HEIGHT,
                      display: 'flex',
                      flexDirection: 'column',
                      minWidth: 0,
                      boxSizing: 'border-box'
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 15, fontWeight: 'bold', mb: 1 }}
                    >
                      市場規模推移
                    </Typography>
                    {isGraphLoading ? (
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        flex={1}
                        minHeight={0}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexDirection: 'column'
                          }}
                        >
                          <p>グラフの読み込み中…</p>
                          <CircularProgress />
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          minWidth: 0,
                          display: 'flex',
                          alignItems: 'stretch'
                        }}
                      >
                        <CategoryTotal yokoziku={yokoziku} />
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* 右：市場規模詳細（表） */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    minWidth: 0,
                    maxWidth: '100%'
                  }}
                >
                  <Box
                    bgcolor="#FFFFFF"
                    borderRadius={3}
                    sx={{
                      p: 2,
                      minHeight: ROW1_HEIGHT,
                      display: 'flex',
                      flexDirection: 'column',
                      minWidth: 0,
                      boxSizing: 'border-box'
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 15, fontWeight: 'bold', mb: 1 }}
                    >
                      市場規模詳細
                    </Typography>

                    {salesHistoryStatus === 'loading' ? (
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        flex={1}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexDirection: 'column'
                          }}
                        >
                          <p>表の読み込み中…</p>
                          <CircularProgress />
                        </Box>
                      </Box>
                    ) : Array.isArray(normalizedHistory) &&
                      normalizedHistory.length > 0 ? (
                      <TableContainer
                        component={Paper}
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          minWidth: 0,
                          overflow: 'auto'
                        }}
                      >
                        <Table size="small" sx={{ minWidth: 360 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                年月
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                売上
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                前年売上
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                前年比
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {historySortedDesc.map((row, idx) => {
                              const sales = row.sales;
                              const salesPrev = row.sales_last_year;
                              const yoy =
                                Number.isFinite(sales) &&
                                Number.isFinite(salesPrev) &&
                                salesPrev > 0
                                  ? (sales / salesPrev - 1) * 100
                                  : null;
                              return (
                                <TableRow key={idx}>
                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {row.ym || '—'}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{ whiteSpace: 'nowrap' }}
                                  >
                                    {fmtYen(sales)}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{ whiteSpace: 'nowrap' }}
                                  >
                                    {fmtYen(salesPrev)}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{ whiteSpace: 'nowrap' }}
                                  >
                                    {yoy != null ? `${yoy.toFixed(2)}%` : 'N/A'}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        flex={1}
                      >
                        <Typography>データがありません。</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* 2段目：円グラフ｜カテゴリ別サマリー（パンくずは枠外） */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                  gap: 3,
                  mb: 3,
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0
                }}
              >
                {/* 左：円グラフ */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    minWidth: 0,
                    maxWidth: '100%'
                  }}
                >
                  <Box
                    bgcolor="#FFFFFF"
                    borderRadius={3}
                    sx={{
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      minHeight: '400px',
                      minWidth: 0,
                      maxWidth: '100%',
                      overflow: 'hidden',
                      boxSizing: 'border-box'
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        flexWrap: 'wrap',
                        mb: 1,
                        minWidth: 0
                      }}
                    >
                      <Typography sx={{ fontSize: 15, fontWeight: 'bold' }}>
                        {viewType === 'top5'
                          ? 'TOP5商品内訳'
                          : 'カテゴリ売上内訳（上位5カテゴリー）'}
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                          value={viewType}
                          onChange={(e) => setViewType(e.target.value)}
                        >
                          <MenuItem value="category">カテゴリー</MenuItem>
                          <MenuItem value="top5">商品</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    <SummaryPie
                      data={pieData}
                      loading={
                        viewType === 'top5'
                          ? isRankLoading
                          : isSummaryLoading
                      }
                      isLeafSelected={
                        viewType === 'category' ? isLeafSelected : false
                      }
                      title=""
                      showLegend={viewType !== 'top5'}
                    />
                  </Box>
                </Box>

                {/* 右：カテゴリ別サマリー */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    minWidth: 0,
                    maxWidth: '100%'
                  }}
                >
                  <Box
                    bgcolor="#FFFFFF"
                    borderRadius={3}
                    sx={{
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      minHeight: '400px',
                      minWidth: 0,
                      boxSizing: 'border-box'
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1,
                        minWidth: 0
                      }}
                    >
                      <Typography sx={{ fontSize: 15, fontWeight: 'bold' }}>
                        カテゴリ別サマリー
                      </Typography>
                      <Tooltip title="CSVダウンロード">
                        <IconButton
                          sx={{ pr: 3 }}
                          aria-label="download csv"
                          size="small"
                          onClick={downloadCategorySummaryCsv}
                        >
                          {downloadIconSrc || download_icon ? (
                            <img
                              src={downloadIconSrc || download_icon}
                              alt="download csv"
                              style={{ width: 18, height: 18 }}
                            />
                          ) : (
                            <DownloadIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
                      {renderSummaryTable(isSummaryLoading)}
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* 3段目：ランキング */}
              <Box mb={3} display="flex" justifyContent="center">
                <Box
                  width="100%"
                  bgcolor="#FFFFFF"
                  borderRadius={3}
                  sx={{ p: 2, minWidth: 0, boxSizing: 'border-box' }}
                >
                  {isRankLoading ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'column'
                      }}
                    >
                      <p>ランキングの読み込み中…</p>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <RankingTotal yokoziku={yokoziku} />
                  )}
                </Box>
              </Box>

              {/* 4段目：検索上位キーワード（Amazon のみ） */}
              {channel === 1 && (
                <Box mb={2} display="flex" justifyContent="center">
                  <CategorySearchNeedsSection accountId={accountId} />
                </Box>
              )}
            </>
          ) : (
            <FreePlanRegisterModal
              updateFreePlanRegisterInfosSuccessFlag={
                updateFreePlanRegisterInfosSuccessFlag
              }
              categories={categories}
            />
          )}
        </Container>
      </Box>
    </>
  );
};

export default NewCategoryboard;
